/**
 * <kazee-product> — PDP buy-box interactions (no framework).
 * Handles gallery thumbnails, finish swatches → variant + price, the quantity
 * stepper, and the plan toggle. Add-to-cart is a native form POST, so it works
 * even if this script fails to load.
 */
if (!customElements.get('kazee-product')) {
  customElements.define(
    'kazee-product',
    class KazeeProduct extends HTMLElement {
      connectedCallback() {
        this.variants = this._json('[data-kz-variants]');
        this.variantMedia = this._json('[data-kz-variant-media]', {});
        this.thumbsEl = this.querySelector('[data-kz-thumbs]');
        this.badgesEl = this.querySelector('.kz-pd__badges');
        this.idInput = this.querySelector('[data-kz-id]');
        this.qtyInput = this.querySelector('[data-kz-qty-input]');
        this.qtyDisplay = this.querySelector('[data-kz-qty-display]');
        this.priceEl = this.querySelector('[data-kz-price]');
        this.totalEl = this.querySelector('[data-kz-total]');
        this.finishName = this.querySelector('[data-kz-finish-name]');
        this.mainImg = this.querySelector('[data-kz-main]');
        this.unitPrice = Number(this.dataset.unitPrice || 0);

        this.querySelectorAll('[data-kz-thumb]').forEach((t) =>
          t.addEventListener('click', () => this._thumb(t))
        );
        this.querySelectorAll('[data-kz-swatch]').forEach((s) =>
          s.addEventListener('click', () => this._swatch(s))
        );
        this.querySelectorAll('[data-kz-plan]').forEach((p) =>
          p.addEventListener('click', () => this._plan(p))
        );
        this.prevBtn = this.querySelector('[data-kz-prev]');
        this.nextBtn = this.querySelector('[data-kz-next]');
        this.prevBtn?.addEventListener('click', () => this._step(-1));
        this.nextBtn?.addEventListener('click', () => this._step(1));
        this._syncNav();
        this._syncBadges();

        this.querySelector('[data-kz-dec]')?.addEventListener('click', () => this._qty(-1));
        this.querySelector('[data-kz-inc]')?.addEventListener('click', () => this._qty(1));

        this.form = this.querySelector('[data-kz-form]');
        this.form?.addEventListener('submit', (e) => this._addToCart(e));

        this._watchSellingPlan();
        this._total();
      }

      /**
       * The Appstle widget writes the chosen plan id onto its own radio/select
       * and never notifies the theme, so the button total has to re-read the
       * form after every interaction. Recompute on a tick delay (Appstle sets
       * the value in its own change handler) and observe the widget subtree,
       * which Appstle re-renders on variant change.
       */
      _watchSellingPlan() {
        const widget = this.querySelector('.kz-pd__subwidget');
        if (!widget) return;

        const recompute = () => {
          this._total();
          setTimeout(() => this._total(), 0);
          setTimeout(() => this._total(), 300);
        };

        widget.addEventListener('change', recompute);
        widget.addEventListener('click', recompute);

        let pending;
        new MutationObserver(() => {
          clearTimeout(pending);
          pending = setTimeout(() => this._total(), 60);
        }).observe(widget, { childList: true, subtree: true, attributes: true });
      }

      /**
       * Tell the Appstle widget the variant changed, so its plan prices follow
       * the finish swatch. It watches the `?variant=` URL and a bubbling change
       * on the id input; without both it keeps rendering the first variant.
       */
      _announceVariant(id) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState({}, '', url);
        } catch (_) {}
        this.idInput?.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /** Plan id currently selected in the Appstle widget ('' = one-time). */
      _sellingPlanId() {
        const checked = this.querySelector('input[name="selling_plan"]:checked');
        if (checked) return checked.value || checked.dataset.appstleRealValue || '';
        const values = [...this.querySelectorAll('[name="selling_plan"]')]
          .map((el) => el.value)
          .filter(Boolean);
        return values.length ? values[values.length - 1] : '';
      }

      /** Price of one unit, discounted when a subscription plan is selected. */
      _unitPrice() {
        const id = Number(this.idInput?.value || 0);
        const variant = this.variants.find((v) => v.id === id);
        if (!variant) return this.unitPrice;

        const planId = this._sellingPlanId();
        if (planId) {
          const alloc = (variant.selling_plan_allocations || []).find(
            (a) => String(a.selling_plan_id) === String(planId)
          );
          if (alloc) return alloc.price;
        }
        return variant.price;
      }

      /**
       * AJAX add-to-cart → opens the Horizon cart drawer. Falls back to the
       * native form POST if the drawer isn't present or the request fails, so
       * the buy button always works. Horizon's cart-items-component re-renders
       * itself when it receives a `cart:update` event with no section payload.
       */
      async _addToCart(e) {
        const drawer = document.querySelector('cart-drawer-component');
        if (!drawer || typeof drawer.open !== 'function') return; // native POST

        e.preventDefault();
        const btn = this.form.querySelector('[type="submit"]');
        if (btn) btn.setAttribute('aria-busy', 'true');

        try {
          const body = new FormData(this.form);
          // Appstle injects duplicate `selling_plan` inputs (one empty); keep the
          // last non-empty value so subscriptions add correctly.
          const plans = body.getAll('selling_plan').filter((v) => v);
          body.delete('selling_plan');
          if (plans.length) body.append('selling_plan', plans[plans.length - 1]);

          const addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add';
          const res = await fetch(addUrl + '.js', {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          if (!res.ok) throw new Error('cart/add failed: ' + res.status);

          let cart = null;
          try {
            cart = await (await fetch('/cart.js')).json();
          } catch (_) {}

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: {
                resource: cart,
                sourceId: 'kazee-product',
                data: { source: 'kazee-product', itemCount: cart && cart.item_count, sections: {} },
              },
            })
          );
          drawer.open();
        } catch (err) {
          this.form.submit(); // hard fallback: full-page add-to-cart
        } finally {
          if (btn) btn.removeAttribute('aria-busy');
        }
      }

      _json(sel, fallback) {
        const empty = fallback === undefined ? [] : fallback;
        try {
          const raw = this.querySelector(sel)?.textContent;
          return raw ? JSON.parse(raw) : empty;
        } catch (e) {
          return empty;
        }
      }

      /**
       * Rebuilds the gallery for a variant from the map Liquid emitted. Each
       * variant resolves to its metafield images, else its featured image plus
       * the rest of the product shots — so there is always something to show.
       */
      _gallery(variantId) {
        const set = this.variantMedia[String(variantId)];
        if (!set || !set.length || !this.thumbsEl) return;

        this.thumbsEl.innerHTML = set
          .map(
            (m, i) =>
              `<button type="button" class="kz-pd__thumb${i === 0 ? ' is-active' : ''}" data-kz-thumb` +
              ` data-src="${this._attr(m.full)}" data-alt="${this._attr(m.alt || '')}"` +
              ` aria-label="View image ${i + 1}">` +
              `<img src="${this._attr(m.thumb)}" alt="" loading="lazy" width="74" height="74"></button>`
          )
          .join('');

        this.thumbsEl.querySelectorAll('[data-kz-thumb]').forEach((t) =>
          t.addEventListener('click', () => this._thumb(t))
        );

        if (this.mainImg) {
          this.mainImg.src = set[0].full;
          if (set[0].alt) this.mainImg.alt = set[0].alt;
        }
        this._syncNav();
        this._syncBadges();
      }

      /** Moves the gallery one image along, wrapping at either end. */
      _step(dir) {
        const thumbs = [...this.querySelectorAll('[data-kz-thumb]')];
        if (thumbs.length < 2) return;
        const current = thumbs.findIndex((t) => t.classList.contains('is-active'));
        const next = ((current < 0 ? 0 : current) + dir + thumbs.length) % thumbs.length;
        this._thumb(thumbs[next]);
      }

      /**
       * The feature pills sit over the pack shot, where there is empty space
       * for them. On the other images they cover the content, so they only
       * show while the first image is active.
       */
      _syncBadges() {
        if (!this.badgesEl) return;
        const thumbs = [...this.querySelectorAll('[data-kz-thumb]')];
        const index = thumbs.findIndex((t) => t.classList.contains('is-active'));
        this.badgesEl.classList.toggle('is-hidden', thumbs.length > 1 && index > 0);
      }

      /** Arrows are pointless on a variant that resolves to one image. */
      _syncNav() {
        const many = this.querySelectorAll('[data-kz-thumb]').length > 1;
        [this.prevBtn, this.nextBtn].forEach((b) => {
          if (b) b.hidden = !many;
        });
      }

      _attr(value) {
        return String(value == null ? '' : value).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      }

      _thumb(t) {
        const src = t.getAttribute('data-src');
        const alt = t.getAttribute('data-alt');
        if (this.mainImg && src) this.mainImg.src = src;
        if (this.mainImg && alt) this.mainImg.alt = alt;
        this.querySelectorAll('[data-kz-thumb]').forEach((x) => x.classList.toggle('is-active', x === t));
        this._syncBadges();
      }

      _swatch(s) {
        this.querySelectorAll('[data-kz-swatch]').forEach((x) => x.classList.toggle('is-active', x === s));
        const name = s.getAttribute('data-name');
        if (this.finishName && name) this.finishName.textContent = name;

        const value = s.getAttribute('data-value');
        if (!value) {
          this._total();
          return;
        }
        const match =
          this.variants.find((v) => v.options && v.options[0] === value && v.available) ||
          this.variants.find((v) => v.options && v.options[0] === value);
        if (match) {
          if (this.idInput) this.idInput.value = match.id;
          this.unitPrice = match.price;
          if (this.priceEl) this.priceEl.textContent = this._money(match.price);
          this._gallery(match.id);
          this._announceVariant(match.id);
        }
        this._total();
      }

      _plan(p) {
        this.querySelectorAll('[data-kz-plan]').forEach((x) => x.classList.toggle('is-active', x === p));
      }

      _qty(delta) {
        let q = parseInt(this.qtyInput?.value || '1', 10) + delta;
        q = Math.max(1, Math.min(99, q));
        if (this.qtyInput) this.qtyInput.value = q;
        if (this.qtyDisplay) this.qtyDisplay.textContent = q;
        this._total();
      }

      _total() {
        const q = parseInt(this.qtyInput?.value || '1', 10);
        if (this.totalEl) this.totalEl.textContent = this._money(this._unitPrice() * q);
      }

      _money(cents) {
        const amount = cents / 100;
        const value = amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
        return '$' + value;
      }
    }
  );
}
